import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import base64 from 'base-64'
import { Data, Organisations } from '../types'
import { useGetOrganisations } from '../hooks/organisations'
import { setClientID } from '@reapit-sales/api-v2'
import { Loader, MainContainer, PageContainer } from '@reapit/elements'
import { useGetUserLastSelectedOrg } from '../hooks'
import { usePutUserLastSelectedOrg } from '../hooks/userSettings/lastSelectedOrg'

interface OrganisationsContextType {
  organisations: Organisations | undefined
  selectedOrganisation: Data | undefined
  setSelectedOrganisation: React.Dispatch<React.SetStateAction<Data | undefined>>
}

export const currentTabOrganisationId = 'currentTabOrganisationId'

const OrganisationsContext = createContext<OrganisationsContextType | undefined>(undefined)

export const OrganisationsProvider = ({ children }: { children: React.ReactNode }) => {
  // List of organisations user has access to
  const {
    data: organisationsData,
    isLoading: isLoadingOrganisations,
    isError: isErrorOrganisations,
    error: errorOrganisations,
  } = useGetOrganisations()

  // Check if the user has access to more than one organisation
  // This is used to determine if we should fetch the last selected organisation
  const hasMultipleOrganisations = (organisationsData?.data ?? []).length > 1

  // Last selected organisation user has selected
  // Only make this request if there are multiple organisations
  const {
    data: lastSelectedOrgData,
    isLoading: isLoadingLastSelectedOrg,
    isError: isErrorLastSelectedOrg,
    error: errorLastSelectedOrg,
  } = useGetUserLastSelectedOrg(undefined, hasMultipleOrganisations)

  const [selectedOrganisation, setSelectedOrganisation] = useState<Data | undefined>(undefined)

  const { mutate: putUserLastSelectedOrg } = usePutUserLastSelectedOrg()

  // Update selectedOrganisation when data changes
  useEffect(() => {
    if (organisationsData?.data) {
      const getCurrentTabOffice = sessionStorage.getItem(currentTabOrganisationId)

      // Match the organisation by ID
      // If getCurrentTabOffice is set, we use that to find the organisation (when user refreshes the tab)
      // Otherwise, we use the last selected organisation data if available
      // If neither is set, we default to the first organisation in the list
      // This ensures that the selected organisation is always set to a valid organisation
      // This is particularly useful in multi-tab scenarios where the user might switch tabs,
      // and we want to ensure the last selected organisation is set correctly for each tab.
      const matchedOrganisation = organisationsData.data.find(
        (org) => org.id === (getCurrentTabOffice || lastSelectedOrgData?.id),
      )
      const organisationToSet = matchedOrganisation || organisationsData.data[0]

      // If an organisation is found to set, we update the selectedOrganisation state
      // and set the client ID for the current session
      // We also check if isLoadingLastSelectedOrg is false to avoid setting the organisation
      // while the last selected organisation is still being fetched
      if (organisationToSet && !isLoadingLastSelectedOrg) {
        setSelectedOrganisation(organisationToSet)
        const xClientID = base64.encode(organisationToSet.url || '')
        setClientID(xClientID)

        // If no sessionstorage for getCurrentTabOffice is set, we assume the user has no last selected organisation or
        // it's the first tab user is log in into where getCurrentTabOffice is not set
        // so we set the current tab organisation ID to the organisation that is being set
        // This is particularly useful in multi-tab scenarios where the user might switch tabs,
        // and we want to ensure the last selected organisation is set correctly for each tab.
        if (!getCurrentTabOffice && lastSelectedOrgData?.id && hasMultipleOrganisations) {
          sessionStorage.setItem(currentTabOrganisationId, organisationToSet.id || '')
        }

        const userNotFound = errorLastSelectedOrg?.response?.status === 404
        // Check if last selected organisation is not set, so response is undefined
        // and if the user was not found i.e 404 error
        // and if the user has access to more than one organisation
        // All conditions are true when the user has no last selected organisation stored (id is undefined)
        // and the backend responds with a 404, indicating the user record does not exist yet.
        // and the user has access to more than one organisation.
        // In this case, we set the last selected organisation to the one that is being set.
        if (!lastSelectedOrgData?.id && userNotFound && hasMultipleOrganisations) {
          putUserLastSelectedOrg({ id: organisationToSet.id })
        }
      }
    }
  }, [organisationsData, lastSelectedOrgData, isLoadingLastSelectedOrg])

  const value = useMemo(() => {
    return {
      organisations: organisationsData,
      selectedOrganisation,
      setSelectedOrganisation, // Expose setter to update selectedOrganisation
    }
  }, [organisationsData, selectedOrganisation])

  const isLoading = isLoadingOrganisations || isLoadingLastSelectedOrg
  const isError = isErrorOrganisations || isErrorLastSelectedOrg
  const error = errorOrganisations || errorLastSelectedOrg

  if (isLoading) {
    return (
      <MainContainer hasGreyBackground>
        <PageContainer>
          <Loader fullPage />
        </PageContainer>
      </MainContainer>
    )
  }

  if (isError) {
    // Error handling already done in the Api hooks
    // This is just for logging purposes for now
    console.error('Error fetching organisations or last selected organisation:', error)
  }

  return <OrganisationsContext.Provider value={value}>{children}</OrganisationsContext.Provider>
}

export const useOrganisationsContext = () => {
  const context = useContext(OrganisationsContext)
  if (!context) {
    throw new Error('useOrganisationsContext must be used within an OrganisationsProvider')
  }
  return context
}
